import { useCallback, useEffect, useRef, useState } from "react"
import {
  useConfirmUploadMutation,
  usePresignedUploadMutation,
  useSetAssetThumbnailMutation,
  type AssetSourceType,
} from "~/ui-rtk/api/assetsApi"
import {
  captureVideoFrameFromUrl,
  fileToDataUrl,
  generateThumbnail,
} from "~/ui-rtk/utils/generate-thumbnail"
import {
  assetToScrapedImage,
  ImageCategory,
  type ScrapedImage,
} from "~/ui-rtk/pages/AdForge/types"

/**
 * PUT a file's bytes straight to a presigned S3 URL. The Content-Type MUST
 * match the type the URL was signed with, or S3 rejects the PUT with 403.
 * Bytes go browser → S3 directly, bypassing API Gateway's 10MB payload cap.
 */
async function putToS3(
  url: string,
  file: File,
  contentType: string,
): Promise<void> {
  const res = await fetch(url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": contentType },
  })
  if (!res.ok) {
    throw new Error(`S3 upload failed (${res.status})`)
  }
}

// Browsers occasionally report an empty `File.type` (notably .mov on
// Windows). Resolve it from the extension so the presign request and the
// S3 PUT still carry a real content type — both must use the SAME value or
// S3 rejects the PUT with 403. Keep in sync with the backend's
// ALLOWED_UPLOAD_MIME_TYPES.
const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  avif: "image/avif",
  gif: "image/gif",
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
}

const resolveFileType = (file: File): string => {
  if (file.type) return file.type
  const extension = file.name.split(".").pop()?.toLowerCase() ?? ""
  return MIME_BY_EXTENSION[extension] ?? ""
}

/**
 * Measure a file's pixel dimensions in the browser. Presigned uploads never
 * pass through the API, so this is the only place the size can be captured —
 * without it the asset row keeps a null resolution and the editor can't show
 * a size or aspect ratio for anything the user uploaded.
 *
 * Resolves to null on anything that won't decode (corrupt file, exotic codec);
 * a missing measurement must never block the upload.
 */
async function measureDimensions(
  file: File,
  fileType: string,
): Promise<{ width: number; height: number } | null> {
  const isVideo = fileType.startsWith("video/")
  const url = URL.createObjectURL(file)
  try {
    return await new Promise<{ width: number; height: number } | null>(
      resolve => {
        const done = (dims: { width: number; height: number } | null): void =>
          resolve(dims && dims.width > 0 && dims.height > 0 ? dims : null)
        if (isVideo) {
          const video = document.createElement("video")
          video.preload = "metadata"
          video.muted = true
          video.onloadedmetadata = () =>
            done({ width: video.videoWidth, height: video.videoHeight })
          video.onerror = () => done(null)
          video.src = url
          return
        }
        const img = new Image()
        img.onload = () =>
          done({ width: img.naturalWidth, height: img.naturalHeight })
        img.onerror = () => done(null)
        img.src = url
      },
    )
  } finally {
    URL.revokeObjectURL(url)
  }
}

export interface UploadFileOptions {
  /** Defaults to "UPLOAD" — pass "GEN_AI" for AI-generated content. */
  sourceType?: AssetSourceType
  /** Link the new asset to a source (e.g. the asset the AI extended from). */
  parentAssetId?: string
  /** Stored on the asset row; useful for tagging variants by aspect ratio. */
  metadata?: Record<string, unknown>
  /** Feed `item_group_id` — tags the new asset into that product's pool. */
  productRefId?: string
}

export interface UseAssetUploadResult {
  uploadFiles: (
    files: File[],
    options?: UploadFileOptions,
  ) => Promise<ScrapedImage[]>
  isUploading: boolean
}

/**
 * Upload files to the asset library and return ScrapedImage entries.
 * On failure for a given file, falls back to an object-URL local image
 * so the user can still use it within the session (but it won't persist).
 *
 * Blob URLs created by the fallback path are kept alive for the lifetime
 * of the consuming component (the browser holds the file bytes until
 * `URL.revokeObjectURL` runs) and freed in bulk on unmount. We don't try
 * to revoke per-asset on removal because the hook doesn't observe what
 * callers do with the returned ScrapedImage — unmount is the right scope
 * to bound the leak.
 */
export default function useAssetUpload(): UseAssetUploadResult {
  const [presignedUpload, { isLoading: isPresigning }] =
    usePresignedUploadMutation()
  const [confirmUpload, { isLoading: isConfirming }] =
    useConfirmUploadMutation()
  const [setAssetThumbnail] = useSetAssetThumbnailMutation()
  // Number of in-flight `uploadFiles` batches. The RTK mutation flags
  // (isPresigning/isConfirming) only cover the presign + confirm calls — they
  // go quiet during the actual S3 PUT (step 2), which is the long part for a
  // video. Counting active batches keeps `isUploading` true across the WHOLE
  // flow (presign → S3 PUT → confirm) so the spinner doesn't flicker off
  // mid-upload. A counter (not a boolean) so overlapping calls don't clear
  // each other early.
  const [activeUploads, setActiveUploads] = useState(0)
  // Tracks every fallback blob URL we've handed out so we can revoke them
  // when the component using this hook unmounts. Without this each
  // upload failure permanently pins the full file bytes in memory.
  const blobUrlsRef = useRef<string[]>([])

  useEffect(
    () => () => {
      for (const url of blobUrlsRef.current) URL.revokeObjectURL(url)
      blobUrlsRef.current = []
    },
    [],
  )

  const uploadFiles = useCallback(
    async (files: File[], options?: UploadFileOptions) => {
      const uploaded: ScrapedImage[] = []
      setActiveUploads(n => n + 1)
      try {
        for (const file of files) {
          const fileType = resolveFileType(file)
          try {
            const thumbnail = await generateThumbnail(file)
            const dimensions = await measureDimensions(file, fileType)
            // 1. Reserve an asset + get presigned S3 PUT URLs.
            const presigned = await presignedUpload({
              fileName: file.name,
              fileType,
              ...(thumbnail && { thumbnailType: thumbnail.type }),
              sourceType: options?.sourceType ?? "UPLOAD",
              ...(options?.parentAssetId && {
                parentAssetId: options.parentAssetId,
              }),
              ...(options?.metadata && {
                metadata: JSON.stringify(options.metadata),
              }),
              ...(options?.productRefId && {
                productRefId: options.productRefId,
              }),
            }).unwrap()
            // 2. Push the bytes straight to S3 (bypasses API Gateway's 10MB cap).
            await putToS3(presigned.fileUploadUrl, file, fileType)
            if (thumbnail && presigned.thumbnailUploadUrl) {
              await putToS3(
                presigned.thumbnailUploadUrl,
                thumbnail,
                thumbnail.type,
              )
            }
            // 3. Flip the asset to SYNCED and get back the enriched record.
            let asset = await confirmUpload({
              assetId: presigned.assetId,
              ...(dimensions && dimensions),
            }).unwrap()
            // 4. Videos whose local frame-grab failed at presign time (exotic
            //    codec, no `loadeddata` before the timeout) get one retry from
            //    the now-hosted copy — the transcoded S3 URL often decodes
            //    where the raw File didn't. Strictly best-effort: a missing
            //    thumbnail must never fail the upload.
            if (!thumbnail && fileType.startsWith("video/") && asset.url) {
              try {
                const frame = await captureVideoFrameFromUrl(asset.url)
                if (frame) {
                  const imageBase64 = await fileToDataUrl(frame)
                  asset = await setAssetThumbnail({
                    assetId: asset.id,
                    imageBase64,
                  }).unwrap()
                }
              } catch {
                // Leave the asset without a thumbnail.
              }
            }
            uploaded.push(assetToScrapedImage(asset))
          } catch (err) {
            // Fallback: local-only ScrapedImage so user isn't blocked
            const blobUrl = URL.createObjectURL(file)
            blobUrlsRef.current.push(blobUrl)
            const isVideo = fileType.startsWith("video/")
            uploaded.push({
              id: Math.random().toString(36).slice(2, 11),
              url: blobUrl,
              thumbnailUrl: blobUrl,
              type: isVideo ? "video" : "image",
              category: ImageCategory.OTHER,
              color: "Default",
              selected: true,
              sourceUrl: "Upload (not persisted)",
            })
            // Surface error in console for debugging; UI reports as success
            // because the local-only asset is usable for immediate launch.
            console.error(`Upload failed for ${file.name}:`, err)
          }
        }
        return uploaded
      } finally {
        setActiveUploads(n => n - 1)
      }
    },
    [presignedUpload, confirmUpload, setAssetThumbnail],
  )

  return {
    uploadFiles,
    // True for the whole flow — presign, S3 PUT, and confirm — not just the
    // two RTK mutations (which leave a gap during the long S3 PUT).
    isUploading: activeUploads > 0 || isPresigning || isConfirming,
  }
}
